import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import CustomDropdown from "../../../../custom/customDropdown";
import { AuthContext } from "../../../../Header/auth - context";
import { uzmiGresku } from "../../../../custom/greske";
import PregledDokumenata from "../../../../custom/PregledDokumenata";
import useVisinaTabele from "../../../../custom/visinaTabele";

/* Registar dokumenata po vozilu.

   Spisak vozila poštuje mrežu firmi (config.CompanyInCompany), isto kao svuda
   drugde — operater vidi svoja i vozila povezanih firmi, ništa više.

   Tabela se puni TEK kad je vozilo izabrano: prazna tabela bez izbora ne kaže
   ništa, a upit bez vozila nema smisla.

   Vozilo izabrano u Pregledu se preuzima iz store-a, pa se dolaskom na ovaj tab
   odmah vidi njegova dokumenta — isti obrazac kao Pregled → Unos. */

const VozilaDokumenta = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const vehicle = useSelector(state => state.vehicle);
    const company = useSelector(state => state.company);
    const dispatch = useDispatch();

    const [vozilaDB, setVozilaDB] = useState([]);
    const [idVozila, setIdVozila] = useState(null);
    const [dokumenta, setDokumenta] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [pregledIndeks, setPregledIndeks] = useState(null);   // otvoren dokument u pregledniku
    const [first, setFirst] = useState(0);
    const [brisem, setBrisem] = useState(null);                 // putanja dokumenta koji se briše
    const [osvezi, setOsvezi] = useState(0);                    // brojač koji ponovo učitava spisak
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([dokumenta]);

    const zovi = async (telo) => {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + "/vehicles/ManageVehicleDocuments", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        if (!res.ok) throw res;
        return res.json();
    };

    /* ── spisak vozila ───────────────────────────────────────────────── */
    useEffect(() => {
        let ziv = true;
        (async () => {
            try {
                const data = await zovi({ step: "INIT" });
                if (!ziv) return;
                const parsed = JSON.parse(data?.[0]?.JsonOutput || '{"VozilaDB":[]}');
                setVozilaDB(parsed.VozilaDB || []);
            } catch (e) {
                const g = await uzmiGresku(e, t("admin.dokVozilaGreska"));
                toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
            }
        })();
        return () => { ziv = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Vozilo izabrano u Pregledu — preuzima se jednom, kad spisak stigne, da se
       ne pregazi kasniji ručni izbor na ovom tabu.

       U spisku su samo aktivna vozila, a Pregled prikazuje i pasivna. Kad se u
       Pregledu izabere pasivno pa se dođe ovde, ono se neće naći — tada ide ista
       poruka kao u Unosu i izbor se poništava, da ne ostane "izabrano" nešto što
       se nigde ne vidi. */
    useEffect(() => {
        if (!vehicle || !vozilaDB.length || idVozila != null) return;

        if (vozilaDB.some(v => String(v.value) === String(vehicle))) {
            setIdVozila(vehicle);
            return;
        }

        toast.current?.show({
            severity: "info",
            summary: t("admin.m_obavestenje_1"),
            detail: t("admin.m_pasivno_vozilo"),
            life: 3000,
        });
        dispatch({ type: "VEHICLE", value: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle, vozilaDB]);

    /* ── dokumenta izabranog vozila ──────────────────────────────────── */
    useEffect(() => {
        setPregledIndeks(null);   // promena vozila zatvara otvoren dokument
        if (idVozila == null) { setDokumenta([]); return; }
        let ziv = true;
        (async () => {
            setUcitava(true);
            try {
                const data = await zovi({ step: "GET", id_vozilo: idVozila });
                if (!ziv) return;
                /* Procedura vraća JSON niz u prvoj koloni prvog reda; kad vozilo
                   nema nijedan dokument, to je prazan niz. */
                const sirovo = data?.[0] ? Object.values(data[0])[0] : "[]";
                const niz = JSON.parse(sirovo || "[]");
                setDokumenta(niz);
                /* Brisanjem poslednjeg reda na zadnjoj strani ta strana prestaje
                   da postoji, a paginator bi ostao na njoj i tabela bi izgledala
                   prazna. Tada — i samo tada — vraća se na prvu stranu. */
                setFirst((prev) => (prev >= niz.length ? 0 : prev));
            } catch (e) {
                if (!ziv) return;
                const g = await uzmiGresku(e, t("admin.dokVozilaGreska"));
                toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
                setDokumenta([]);
            } finally {
                if (ziv) setUcitava(false);
            }
        })();
        return () => { ziv = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idVozila, osvezi]);

    /* Putanja iz baze je Windows oblika i vodi u deljeni folder — pretvara se u
       adresu koju servira backend. Isti postupak kao kod zaposlenih. */
    const adresaDokumenta = (putanja) => {
        if (!putanja) return null;
        const relativna = String(putanja).replace(/\\/g, "/").replace(/^C:\/Dokumenta\//, "").replace(/^\/+/, "");
        return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(relativna)}`;
    };

    /* Sam fajl je link, isto kao u Pregledu vozila — nema zasebne kolone sa
       dugmetom "Pregledaj". */
    /* Preglednik i tabela rade nad ISTIM nizom, pa je redosled listanja isti kao
       redosled redova na ekranu — bez toga bi strelice vodile nekim trećim redom. */
    const zaPregled = dokumenta.map(d => ({ ...d, url: adresaDokumenta(d.putanja) }));

    const linkFajla = (red) => {
        if (!adresaDokumenta(red.putanja)) return "-";
        return (
            <Button
                label={red.fajl}
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                /* Pozicija se traži po samom zapisu, ne po rowIndex-u: rowIndex je
                   redni broj u FILTRIRANOM i sortiranom prikazu, pa bi uz aktivnu
                   pretragu strelice listale pogrešne dokumente. */
                onClick={() => setPregledIndeks(zaPregled.findIndex(x => x.putanja === red.putanja))}
                tooltip={t("admin.pregledaj")}
                tooltipOptions={{ position: "top" }}
            />
        );
    };

    /* Brisanje dokumenta: zapis iz evidencije i fajl sa diska.

       Dva ishoda idu kao DVA toasta, jer su to dva posla koja ne moraju da se
       poklope — zapis je obrisan kad server to potvrdi, a fajla na disku može i
       da nema (ručno uklonjen, premešten). To nije greška, ali korisnik mora da
       zna da na disku nije bilo šta da se briše.

       Fajl ne briše frontend nego server, i to samo kad je baza potvrdila zapis
       — ovde se putanja samo prosleđuje nazad onakva kakva je stigla. */
    const obrisiRed = (red) => confirmDialog({
        message: t("admin.dokBrisanjePitanje", { naziv: red?.naziv || red?.fajl || "" }),
        header: t("admin.dokBrisanjeNaslov"),
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        acceptLabel: t("common.da"),
        rejectLabel: t("common.ne"),
        accept: async () => {
            setBrisem(red.putanja);
            try {
                const d = await zovi({ step: "DELETE", id_vozilo: idVozila, putanja: red.putanja });

                if (!d?.obrisano) {
                    toast.current?.show({
                        severity: "warn",
                        summary: t("common.upozorenje"),
                        detail: d?.message || t("admin.dokBrisanjeGreska"),
                        life: 5000,
                    });
                    return;
                }

                toast.current?.show([
                    { severity: "success", summary: t("common.uspeh"), detail: d.message, life: 4000 },
                    d.fajlObrisan
                        ? { severity: "success", summary: t("common.uspeh"), detail: t("admin.dokFajlObrisan"), life: 4000 }
                        : d.fajlPostojao
                            ? { severity: "error", summary: t("common.greska"), detail: t("admin.dokFajlNijeObrisan"), life: 6000 }
                            : { severity: "info", summary: t("admin.m_obavestenje_1"), detail: t("admin.dokFajlNemaNaServeru"), life: 5000 },
                ]);
                setOsvezi((n) => n + 1);
            } catch (e) {
                const g = await uzmiGresku(e, t("admin.dokBrisanjeGreska"));
                toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
            } finally {
                setBrisem(null);
            }
        },
    });

    const dugmeBrisanja = (red) => (
        <Button
            type="button"
            icon="pi pi-trash"
            severity="danger"
            rounded
            /* Puna boja, ne `outlined`: u svetloj temi je obrubljeno crveno
               bledo (pastelna paleta aplikacije) i dugme deluje onemogućeno —
               a onemogućeno je ovde pravo stanje (red bez putanje). */
            loading={brisem === red?.putanja}
            disabled={!red?.putanja}
            onClick={() => obrisiRed(red)}
            tooltip={t("common.delete")}
            tooltipOptions={{ position: "top" }}
        />
    );

    /* Izvoz prati isti obrazac kao Pregled vozila: ista dva stupca koja se vide
       na ekranu, ime fajla sa nazivom firme i vremenom. */
    const exportExcel = async () => {
        if (!dokumenta.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Dokumenta vozila");

        const zaglavlja = [t("admin.nazivDokumenta"), t("admin.dokFajl")];
        const headerRow = worksheet.addRow(zaglavlja);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = {
                top: { style: "medium" }, left: { style: "medium" },
                bottom: { style: "medium" }, right: { style: "medium" },
            };
        });

        dokumenta.forEach((r) => {
            const row = worksheet.addRow([r.naziv ?? "", r.fajl ?? ""]);
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: "thin" }, left: { style: "thin" },
                    bottom: { style: "thin" }, right: { style: "thin" },
                };
            });
        });

        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });

        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");

        const registracija = vozilaDB.find(v => String(v.value) === String(idVozila))?.label || "";

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }), `Dokumenta ${registracija} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const zaglavljeTabele = (
        <div className="export-buttons fakture-stavke-header">
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                        onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            {/* onInput, ne onChange — pretraga mora da reaguje na svaki otkucaj. */}
            <input
                type="search"
                onInput={(e) => setGlobalFilter(e.target.value)}
                placeholder={t("common.search")}
                className="p-inputtext p-component"
            />
        </div>
    );

    return (
        <div className="zaposleni_table_container" id="vozila_dokumenta">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".export-buttons>button" position="bottom" />

            {/* Sirina po sadrzaju (registracije su kratke), ali ne preko 320px —
                i 1% razmaka do tabele, da izbor i spisak ne slepe jedno za drugo. */}
            <div style={{ display: "flex", flexDirection: "column", width: "fit-content", maxWidth: "320px", marginBottom: "1%" }}>
                <label htmlFor="id_vozilo_dok">{t("admin.dokIzaberiVozilo")}:</label>
                {/* Opcije nose polje "value", a PrimeReact ga tada sam uzima kao
                    vrednost izbora — pa se ovde radi sa ID-jem, ne sa celim
                    objektom. Ranije je slato e.target.value.value, sto je nad
                    brojem bilo undefined i izbor nije radio. */}
                <CustomDropdown
                    id="id_vozilo_dok"
                    name="id_vozilo_dok"
                    value={idVozila}
                    options={vozilaDB}
                    optionLabel="label"
                    filter
                    onChange={(e) => setIdVozila(e.target.value ?? null)}
                    placeholder={t("admin.dokIzaberiVozilo")}
                    filterPlaceholder={t("gps.pretraziRegistraciju")}
                    emptyMessage={t("gps.nemaVozila")}
                    /* minWidth: uz fit-content omot dropdown bi se inace skupio
                       na sirinu praznog polja i registracije se ne bi videle. */
                    style={{ width: "100%", minWidth: "220px" }}
                />
            </div>

            {idVozila == null ? (
                <p className="infoPoruka" style={{ margin: "1em 0", display: "flex", alignItems: "center" }}>
                    {t("admin.dokIzaberiPaTabela")}
                </p>
            ) : (
                <>
                    {/* Isti propsi kao tabela u Pregledu vozila, da se dva spiska
                        u istom modulu ne ponasaju razlicito. */}
                    <div ref={omotTabele}>
                    <DataTable className="stavke-tabela"
                        value={dokumenta}
                        loading={ucitava}
                        header={zaglavljeTabele}
                        paginator
                        rows={redovaTabele}
                        first={first}
                        globalFilter={globalFilter}
                        onPage={(e) => setFirst(e.first)}
                        scrollable
                        scrollHeight={visinaTabele}
                        resizableColumns
                        columnResizeMode="expand"
                        emptyMessage={t("admin.dokNemaZaVozilo")}
                    >
                        <Column field="naziv" header={t("admin.nazivDokumenta")} sortable style={{ minWidth: "150px" }} />
                        <Column field="fajl" header={t("admin.dokFajl")} sortable body={linkFajla} style={{ minWidth: "150px" }} />
                        <Column header={t("admin.dokRadnje")} body={dugmeBrisanja} align="center" style={{ minWidth: "90px", maxWidth: "110px" }} />
                    </DataTable>
                    </div>
                </>
            )}

            <PregledDokumenata
                dokumenta={zaPregled}
                indeks={pregledIndeks}
                naslov={vozilaDB.find(v => String(v.value) === String(idVozila))?.label || ""}
                onIndeks={setPregledIndeks}
                onZatvori={() => setPregledIndeks(null)}
            />
        </div>
    );
};

export default VozilaDokumenta;
